import { useEffect, useState } from 'react';
import {
  Coins, Loader2, CreditCard, X, Sparkles, Clock, CheckCircle, QrCode, Wallet as WalletIcon,
  Crown, Star, Flame, Gift, Ticket, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { getRechargeKits, type RechargeKit } from '@/shared/services/cashService';
import { useToast } from '@/shared/contexts/ToastContext';
import {
  order, capture, getRecharges, getGateways,
  type GatewayProvider, type PublicGateway,
} from '@/shared/services/payService';
import { initMercadoPago, Wallet } from '@mercadopago/sdk-react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import EfiPixDialog from './EfiPixDialog';

const PAYPAL_CLIENT_ID = (import.meta.env.VITE_PAYPAL_CLIENT_ID as string) || '';

// Public key do MP usada pelo Wallet do SDK; pode vir do .env do front
// (e diferente da AccessToken do backend, esta nao e secret).
const MP_PUBLIC_KEY = (import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY as string) || '';
if (MP_PUBLIC_KEY) initMercadoPago(MP_PUBLIC_KEY);

function formatBRL(value: number | undefined) {
  return (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function getPrice(kit: RechargeKit): number {
  return kit.brl ?? kit.price ?? kit.value ?? kit.amount ?? 0;
}

function formatDate(d: string) {
  try { return new Date(d).toLocaleString('pt-BR'); } catch { return d; }
}

function gatewayIcon(p: GatewayProvider) {
  if (p === 'EFI') return <QrCode size={28} className="text-[#8fb9de]" />;
  if (p === 'MP') return <WalletIcon size={28} className="text-[#8fb9de]" />;
  return <CreditCard size={28} className="text-[#8fb9de]" />;
}

// Tier do pacote baseado no valor em Cash — progressão como raridade de jogo.
// Thresholds (cash) baseados nos pacotes típicos: 1k/3k/5.5k/12k/26k/68k.
type Tier = {
  color: string;          // hex (sem #)
  badge: string | null;
  badgeIcon: React.ReactNode;
  glow: string;
  iconSize: number;       // px — escala progressiva (mais raro = maior)
};
function tierFor(cash: number): Tier {
  if (cash >= 50000) {
    return { color: 'fbbf24', badge: 'Lendário',  badgeIcon: <Crown size={10} />,    glow: 'rgba(251,191,36,0.45)', iconSize: 144 };
  }
  if (cash >= 20000) {
    return { color: 'a370f7', badge: 'Premium',   badgeIcon: <Star size={10} />,     glow: 'rgba(163,112,247,0.4)', iconSize: 128 };
  }
  if (cash >= 10000) {
    return { color: '3b82f6', badge: 'Recomendado', badgeIcon: <Sparkles size={10} />, glow: 'rgba(59,130,246,0.4)',  iconSize: 120 };
  }
  if (cash >= 5000) {
    return { color: '8fb9de', badge: 'Popular',   badgeIcon: <Flame size={10} />,    glow: 'rgba(79,120,168,0.4)',   iconSize: 112 };
  }
  return     { color: '8fb9de', badge: null,        badgeIcon: null,                  glow: 'rgba(79,120,168,0.25)',  iconSize: 100 };
}

export default function RechargePage() {
  const { cash, refreshCash, account } = useAuth();
  const { notify } = useToast();

  // Celular vinculado (verificado por SMS) — condiciona cartao de credito no MP.
  const phoneVerified = !!account?.isCellphoneVerified;

  const [kits, setKits] = useState<RechargeKit[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [gateways, setGateways] = useState<PublicGateway[]>([]);
  const [loading, setLoading] = useState(true);

  // Payment flow state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [chooseFor, setChooseFor] = useState<{ index: number } | null>(null);
  const [preferenceId, setPreferenceId] = useState('');
  const [paypalOrderId, setPaypalOrderId] = useState('');
  const [paymentProvider, setPaymentProvider] = useState<GatewayProvider | ''>('');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [efiDialog, setEfiDialog] = useState<{ kitId: number; title: string; cash: number } | null>(null);

  // Cupom de streamer (opcional) — normalizado pra MAIÚSCULO/sem espaços.
  const [coupon, setCoupon] = useState('');
  const couponClean = coupon.trim().toUpperCase();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getRechargeKits().catch(() => []),
      getRecharges().catch(() => []),
      getGateways().catch(() => []),
    ]).then(([k, h, g]) => {
      setKits(k);
      setHistory(h);
      setGateways((g || []).filter(x => x.ready));
    }).finally(() => setLoading(false));
  }, []);

  const getKitName = (id: number) => {
    const kit = kits.find((k: any) => k.id === id);
    if (kit?.title) return kit.title;
    if (kit?.cash) return kit.cash.toLocaleString('pt-BR') + ' lcoin';
    return '-';
  };

  // Clicar no kit: decide se vai abrir modal de escolha ou ir direto
  const orderRecharge = (index: number) => {
    const kit: any = kits[index];
    if (!kit) return;
    setSelectedIndex(index);

    if (gateways.length === 0) {
      notify('error', 'Nenhum gateway de pagamento ativo no momento');
      return;
    }

    if (gateways.length === 1) {
      runPayment(gateways[0].provider, index);
      return;
    }

    setChooseFor({ index });
  };

  const runPayment = (provider: GatewayProvider, index: number) => {
    const kit: any = kits[index];
    if (!kit) return;
    setChooseFor(null);

    if (provider === 'EFI') {
      // Mostra o total que o player vai receber (cash + bônus do kit)
      setEfiDialog({ kitId: kit.id, title: kit.title || 'Recarga', cash: (kit.cash || 0) + (kit.bonus || 0) });
      return;
    }
    if (provider === 'MP' || provider === 'PAYPAL') {
      const container = document.getElementById('wallet_container');
      if (container) container.innerHTML = '';
      setPreferenceId('');
      setPaypalOrderId('');
      setPaymentProvider(provider);
      setPaymentOpen(true);
      setPaying(true);
      order({ kit: kit.id, ...(couponClean ? { coupon: couponClean } : {}) })
        .then((res: any) => {
          if (provider === 'MP') {
            setPreferenceId(res.preferenceid ?? '');
          } else {
            // /order ja cria order PayPal embedded (paypal_response)
            const ppId = res.paypal?.id ?? '';
            if (!ppId) {
              notify('error', 'PayPal indisponivel no momento');
              closeRecharge();
              return;
            }
            setPaypalOrderId(ppId);
          }
        })
        .catch((err: any) => {
          notify('error', err?.message || 'Erro ao criar pedido');
          closeRecharge();
        })
        .finally(() => setPaying(false));
      return;
    }
  };

  const closeRecharge = () => {
    setPreferenceId('');
    setPaypalOrderId('');
    setPaymentProvider('');
    setSelectedIndex(-1);
    setPaymentOpen(false);
  };

  const captureOrder = async (orderId: string) => {
    setPaying(true);
    try {
      await capture(orderId);
      refreshCash();
      notify('success', 'Pagamento confirmado!');
      getRecharges().catch(() => []).then(setHistory);
    } catch {
      notify('error', 'Erro ao confirmar pagamento');
    } finally {
      closeRecharge();
      setPaying(false);
    }
  };

  const selectedKit = selectedIndex >= 0 ? kits[selectedIndex] : null;
  const hasBonusKits = kits.some(k => (k.bonus ?? 0) > 0);

  // Card "Bônus de Recarga" — no desktop vira toast fixo no canto direito da
  // tela (fora do container do grid); no mobile é renderizado abaixo do grid.
  const bonusPanel = hasBonusKits ? (
    <div className="relative overflow-hidden rounded-md p-5"
         style={{
           background: 'linear-gradient(180deg, #0b0f12 0%, #060809 100%)',
           border: '1px solid rgba(79,120,168,0.2)',
           boxShadow: '0 8px 24px -14px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.03)',
         }}>
      {/* Glow decorativo */}
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-40 rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(ellipse, rgba(79,120,168,0.12), transparent 70%)' }} />

      <div className="relative text-center mb-4">
        <div className="w-10 h-10 mx-auto rounded-md bg-[#8fb9de]/10 border border-[#8fb9de]/30 flex items-center justify-center mb-2">
          <Gift size={18} className="text-[#8fb9de]" />
        </div>
        <h2 className="text-sm font-black text-white uppercase tracking-[0.12em]">
          Bônus de Recarga
        </h2>
        <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#8fb9de] mt-0.5">
          Como funciona?
        </p>
        <p className="text-[12px] text-white/65 mt-2 leading-relaxed">
          Quanto maior a recarga, mais <span className="text-white font-bold">lcoin</span> você
          recebe. O bônus é creditado <span className="text-white font-bold">automaticamente</span> junto
          com a recarga.
        </p>
      </div>

      <div className="relative space-y-2">
        {kits.filter(k => (k.bonus ?? 0) > 0).map((kit) => {
          const tier = tierFor(kit.cash ?? 0);
          const c = tier.color;
          return (
            <div key={kit.id}
                 className="rounded-md px-3 py-2.5"
                 style={{
                   background: 'rgba(255,255,255,0.03)',
                   border: `1px solid #${c}33`,
                   borderLeft: `3px solid #${c}`,
                 }}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[12px] text-white/85 min-w-0 truncate">
                  <span className="font-bold" style={{ color: `#${c}` }}>{formatBRL(getPrice(kit))}</span>
                  <span className="text-white/40 mx-1.5">→</span>
                  <span className="font-bold text-white">{(kit.cash ?? 0).toLocaleString('pt-BR')}</span>
                </p>
                <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide"
                      style={{
                        background: `linear-gradient(135deg, #${c}, #${c}cc)`,
                        color: '#0a0e14',
                        boxShadow: `0 0 10px ${tier.glow}`,
                      }}>
                  {tier.badgeIcon ?? <Sparkles size={10} />}
                  +{(kit.bonus ?? 0).toLocaleString('pt-BR')}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="relative mt-3 rounded-md px-3 py-2.5 bg-white/[0.03] border border-white/[0.06]">
        <p className="text-[11px] text-white/55 leading-relaxed">
          <span className="text-[#8fb9de] font-bold">Dica:</span> o bônus vem no pacote
          correspondente — um pacote maior rende mais lcoin do que várias recargas pequenas
          pelo mesmo valor. Escolha com estratégia!
        </p>
      </div>
    </div>
  ) : null;

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-bold text-white mb-6">Recarga</h1>

      {!loading && gateways.length === 0 && (
        <div className="surface-card rounded-md p-4 mb-6 border-amber-500/30">
          <p className="text-sm text-amber-400">
            Nenhum gateway de pagamento configurado. Avise um administrador.
          </p>
        </div>
      )}

      {/* Kits Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="animate-spin text-[#8fb9de]" />
        </div>
      ) : kits.length === 0 ? (
        <p className="text-sm text-white/65 text-center py-12">Nenhum pacote disponivel no momento</p>
      ) : (
        <>
        {/* Layout flex: o grid ocupa o espaço que sobra (flex-1) e o card de bônus fica
            na coluna direita (w-80), sticky (acompanha o scroll). O flexbox "calcula o
            espaço e tira do container" automaticamente — responsivo, e NUNCA sobrepõe,
            com ou sem sidebar (diferente do fixed, que erra por causa do deslocamento
            da sidebar). Abaixo de xl vira coluna (card desce pra baixo dos cards). */}
        <div className="flex flex-col xl:flex-row xl:items-start xl:gap-8">
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-14 pt-14 mt-4">
          {kits.map((kit, i) => {
            const tier = tierFor(kit.cash ?? 0);
            const c = tier.color;
            const imgSrc = kit.image ? `/assets/gems/${kit.image}` : null;
            const halfImg = tier.iconSize / 2;
            const cardPt = Math.round(tier.iconSize * 0.5 + 28); // padding-top responsivo ao tamanho do ícone
            return (
              <div
                key={kit.id ?? i}
                className="recharge-card group relative cursor-pointer rounded-md px-6 pb-6 flex flex-col items-center text-center transition-all duration-300"
                style={{
                  paddingTop: `${cardPt}px`,
                  background: `linear-gradient(180deg, #0b0f12 0%, #060809 100%)`,
                  border: `1px solid #${c}33`,
                  boxShadow: `0 8px 24px -14px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.03)`,
                  ['--c' as any]: `#${c}`,
                  ['--c-glow' as any]: tier.glow,
                }}
                onClick={() => orderRecharge(i)}
              >
                {/* Badge no topo direito (se tier tiver) */}
                {tier.badge && (
                  <div className="absolute -top-3 right-4 z-10">
                    <span
                      className="flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest"
                      style={{
                        background: `linear-gradient(135deg, #${c}, #${c}cc)`,
                        color: '#0a0e14',
                        boxShadow: `0 0 18px ${tier.glow}`,
                      }}
                    >
                      {tier.badgeIcon} {tier.badge}
                    </span>
                  </div>
                )}

                {/* Imagem do gema — sobreposta no topo do card. Tamanho escala
                    com o tier (Lendário > Premium > Recomendado > Popular > Normal). */}
                <div
                  className="recharge-img-wrap absolute left-1/2 transition-all duration-300"
                  style={{
                    top: `-${halfImg * 0.55}px`,        // ~55% da imagem vaza pra cima
                    width: `${tier.iconSize}px`,
                    height: `${tier.iconSize}px`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={kit.title || `${kit.cash} lcoin`}
                      className="w-full h-full object-contain select-none pointer-events-none"
                      style={{ filter: `drop-shadow(0 6px 12px rgba(0,0,0,0.6)) drop-shadow(0 0 16px #${c}55)` }}
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-full rounded-md flex items-center justify-center"
                         style={{ background: `radial-gradient(circle, #${c}33, transparent 70%)` }}>
                      <Coins size={tier.iconSize * 0.45} style={{ color: `#${c}` }} />
                    </div>
                  )}
                </div>

                {/* Conteúdo */}
                <div className="mt-4 w-full mb-5">
                  {kit.title && (
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.25em] mb-1" style={{ color: `#${c}` }}>
                      {kit.title}
                    </p>
                  )}
                  {/* Selo de bônus — cash extra creditado junto, na cor do tier */}
                  {(kit.bonus ?? 0) > 0 && (
                    <div className="flex justify-center mt-2 mb-1">
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider"
                        style={{
                          color: `#${c}`,
                          background: `#${c}14`,
                          border: `1px solid #${c}66`,
                          boxShadow: `0 0 14px ${tier.glow}`,
                        }}
                      >
                        {tier.badgeIcon ?? <Sparkles size={10} />}
                        Bônus +{(kit.bonus ?? 0).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  )}
                  <p className="text-4xl font-black text-white tracking-tight leading-none mt-1"
                     style={{ textShadow: `0 0 12px #${c}66` }}>
                    {(kit.cash ?? 0).toLocaleString('pt-BR')}
                  </p>
                  <p className="text-[11px] text-white/55 mt-1 uppercase tracking-[0.25em] font-bold"><span className="lcoin">lcoin</span></p>

                  <div className="h-px w-12 mx-auto my-4" style={{ background: `linear-gradient(to right, transparent, #${c}55, transparent)` }} />

                  <p className="text-2xl font-bold text-white/95">{formatBRL(getPrice(kit))}</p>
                </div>

                {/* mt-auto: pina o botão no rodapé — cards da mesma linha ficam
                    com o botão alinhado mesmo quando só alguns têm selo de bônus */}
                <button
                  disabled={paying || gateways.length === 0}
                  onClick={(e) => { e.stopPropagation(); orderRecharge(i); }}
                  className="recharge-btn mt-auto w-full py-3 rounded-md text-xs font-extrabold uppercase tracking-widest transition-all duration-300 disabled:opacity-50 cursor-pointer"
                  style={{
                    background: `#${c}1a`,
                    color: `#${c}`,
                    border: `1px solid #${c}55`,
                  }}
                >
                  {paying && selectedIndex === i ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Processando…
                    </span>
                  ) : 'Recarregar'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Card de bônus — coluna direita, sticky (acompanha o scroll, respeita a nav via
            top-24). Dentro do flow do flex => nunca sobrepõe os cards, responsivo. */}
        {hasBonusKits && (
          <aside className="hidden xl:block w-80 shrink-0 sticky top-24 mt-[4.5rem] max-h-[calc(100vh-7rem)] overflow-y-auto rounded-md">
            {bonusPanel}
          </aside>
        )}
        </div>

        {/* Abaixo de xl: card de bônus vira seção normal abaixo do grid */}
        {hasBonusKits && (
          <div className="xl:hidden mt-10">
            {bonusPanel}
          </div>
        )}
        </>
      )}

      {/* Estilos do card de recarga — hover potente, glow no botão */}
      <style>{`
        .recharge-card { will-change: transform; }
        .recharge-card:hover {
          transform: translateY(-10px);
          border-color: var(--c) !important;
          box-shadow:
            0 18px 40px rgba(0,0,0,0.65),
            0 0 32px var(--c-glow),
            inset 0 1px 0 rgba(255,255,255,0.08) !important;
        }
        .recharge-card:hover .recharge-img-wrap { transform: translate(-50%, -6px) scale(1.06); }
        .recharge-card:hover .recharge-btn {
          background: var(--c) !important;
          color: #0a0e14 !important;
          border-color: var(--c) !important;
          box-shadow: 0 0 18px var(--c-glow);
        }
      `}</style>

      {/* Historico de Recargas */}
      {history.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-bold text-white mb-4">
            <span className="text-[#8fb9de]">Minhas Recargas</span>
          </h2>
          <div className="surface-card rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-4 py-3 text-[#8fb9de] font-bold text-xs uppercase tracking-wider">Kit</th>
                  <th className="text-left px-4 py-3 text-[#8fb9de] font-bold text-xs uppercase tracking-wider">Valor</th>
                  <th className="text-left px-4 py-3 text-[#8fb9de] font-bold text-xs uppercase tracking-wider">Data</th>
                  <th className="text-left px-4 py-3 text-[#8fb9de] font-bold text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item: any, i: number) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3 text-white/80">{getKitName(item.kitId)}</td>
                    <td className="px-4 py-3 text-white/80">{formatBRL(item.brl)}</td>
                    <td className="px-4 py-3 text-white/60">{formatDate(item.date)}</td>
                    <td className="px-4 py-3">
                      {item.status === 1 ? (
                        <span className="inline-flex items-center gap-1 text-green-400 text-xs font-bold">
                          <CheckCircle size={14} /> Completo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-white/65 text-xs">
                          <Clock size={14} /> Pendente
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de escolha de gateway */}
      {chooseFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
             onMouseDown={(e) => { if (e.target === e.currentTarget) setChooseFor(null); }}>
          <div className="surface-card rounded-md p-6 max-w-md w-full relative"
               onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setChooseFor(null)}
              className="absolute top-4 right-4 text-white/55 hover:text-white cursor-pointer"
            >
              <X size={20} />
            </button>
            <h3 className="text-xl font-bold text-white mb-1">Escolha o pagamento</h3>
            <p className="text-sm text-white/55 mb-5">
              {kits[chooseFor.index]?.title} — <span className="text-[#8fb9de]">{formatBRL(getPrice(kits[chooseFor.index]))}</span>
            </p>
            <div className="space-y-2">
              {gateways.map(g => {
                // MP: se "aceitar cartao" ligado mas o jogador nao tem celular
                // vinculado, cartao fica bloqueado — avisa de forma minimalista.
                const mpNeedsPhone = g.provider === 'MP' && g.acceptCreditCard && !phoneVerified;
                return (
                  <div key={g.provider}>
                    <button
                      onClick={() => runPayment(g.provider, chooseFor.index)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-md
                                 bg-white/[0.04] hover:bg-[#8fb9de]/[0.08]
                                 border border-white/[0.06] hover:border-[#8fb9de]/40
                                 transition cursor-pointer text-left"
                    >
                      <div className="w-10 h-10 rounded-md bg-[#8fb9de]/10 flex items-center justify-center shrink-0">
                        {gatewayIcon(g.provider)}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-white">{g.displayName}</p>
                        <p className="text-[11px] text-white/45">
                          {g.provider === 'EFI' && 'Pagamento via PIX, aprovação instantânea'}
                          {g.provider === 'MP' && (g.acceptCreditCard ? 'MercadoPago — pix, boleto, cartão' : 'MercadoPago — pix, boleto')}
                          {g.provider === 'PAYPAL' && 'Pague com saldo PayPal ou cartão'}
                        </p>
                      </div>
                    </button>
                    {mpNeedsPhone && (
                      <p className="mt-1 ml-1 text-[11px] text-amber-400/90 flex items-center gap-1.5">
                        <AlertTriangle size={12} className="shrink-0" />
                        Vincule seu celular para habilitar cartão de crédito.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Cupom discreto — opcional, no modal do gateway */}
            <div className="mt-4 pt-3 border-t border-white/[0.06]">
              <div className="relative">
                <Ticket size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type="text"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  placeholder="Cupom (opcional)"
                  maxLength={32}
                  spellCheck={false}
                  autoCapitalize="characters"
                  className="w-full pl-8 pr-3 py-2 rounded-md bg-black/30 border border-white/[0.08]
                             text-white text-xs font-mono tracking-widest uppercase placeholder:text-white/25
                             focus:outline-none focus:border-[#9146FF]/50 transition"
                />
              </div>
              {couponClean && (
                <p className="text-[10px] text-[#c8a6ff] mt-1.5 flex items-center gap-1">
                  <CheckCircle size={11} /> Cupom <span className="font-bold font-mono">{couponClean}</span> aplicado.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* EFI Pay PIX Dialog */}
      {efiDialog && (
        <EfiPixDialog
          kitId={efiDialog.kitId}
          kitTitle={efiDialog.title}
          kitCash={efiDialog.cash}
          coupon={couponClean || undefined}
          onClose={() => { setEfiDialog(null); setSelectedIndex(-1); }}
          onPaid={() => {
            refreshCash();
            getRecharges().catch(() => []).then(setHistory);
          }}
        />
      )}

      {/* Payment Modal — Mercado Pago Wallet */}
      {paymentOpen && selectedKit && (
        <>
          <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-md" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
               onMouseDown={(e) => { if (e.target === e.currentTarget && !paying) closeRecharge(); }}>
            <div className="surface-card rounded-md p-8 max-w-md w-full relative" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => !paying && closeRecharge()}
                className="absolute top-4 right-4 text-white/65 hover:text-white transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto rounded-md bg-[#8fb9de]/10 flex items-center justify-center mb-4">
                  <CreditCard size={32} className="text-[#8fb9de]" />
                </div>
                <h3 className="text-xl font-bold text-white">Finalizar Pagamento</h3>
                <p className="text-white/65 text-sm mt-1">
                  <span className="text-white font-bold">{(selectedKit.cash ?? 0).toLocaleString('pt-BR')} lcoin</span>
                  {(selectedKit.bonus ?? 0) > 0 && (
                    <span className="text-amber-400 font-bold"> +{(selectedKit.bonus ?? 0).toLocaleString('pt-BR')} bônus</span>
                  )}
                  {' '} por {' '}
                  <span className="text-white font-bold">{formatBRL(getPrice(selectedKit))}</span>
                </p>
              </div>

              {paymentProvider === 'MP' && preferenceId && (
                <div className="w-full" id="wallet_container">
                  <Wallet
                    initialization={{ preferenceId, redirectMode: 'blank' as any }}
                    customization={{ texts: { valueProp: 'smart_option' } } as any}
                  />
                </div>
              )}

              {paymentProvider === 'PAYPAL' && paypalOrderId && PAYPAL_CLIENT_ID && (
                <div className="w-full">
                  <PayPalScriptProvider
                    options={{
                      clientId: PAYPAL_CLIENT_ID,
                      currency: 'BRL',
                      intent: 'capture',
                    }}
                  >
                    <PayPalButtons
                      style={{
                        layout: 'vertical',
                        color: 'gold',
                        shape: 'rect',
                        label: 'pay',
                      }}
                      createOrder={async () => paypalOrderId}
                      onApprove={async (data) => {
                        await captureOrder(data.orderID);
                      }}
                      onError={() => {
                        notify('error', 'Erro no pagamento PayPal');
                      }}
                      onCancel={() => {
                        notify('error', 'Pagamento PayPal cancelado');
                      }}
                    />
                  </PayPalScriptProvider>
                </div>
              )}

              {paymentProvider === 'PAYPAL' && paypalOrderId && !PAYPAL_CLIENT_ID && (
                <p className="text-center text-sm text-amber-400">
                  VITE_PAYPAL_CLIENT_ID nao configurado no .env do front
                </p>
              )}

              {!preferenceId && !paypalOrderId && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 size={24} className="animate-spin text-[#8fb9de]" />
                  <span className="ml-3 text-sm text-white/65">Preparando pagamento...</span>
                </div>
              )}

              <div className="flex justify-center mt-4">
                <button
                  onClick={() => closeRecharge()}
                  className="text-sm text-red-400 hover:text-red-300 underline underline-offset-2 cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
